// Firebase initialisation + customer-side auth helpers.
//
// We only use the Auth module of the Firebase SDK here. Other modules
// (Firestore, Storage, Messaging) live on the same project but the
// runtime never touches them — keeps the bundle ~120KB instead of the
// 600KB full-SDK monolith.
//
// All config values come from build-time env (`.env.dev` / `.env.prod`),
// inlined by Vite. They are PUBLIC by design: Firebase Auth's security
// comes from authorized-domains, custom claims, and verification flows,
// not from hiding the apiKey.

import { initializeApp, type FirebaseApp } from "firebase/app";
import {
	getAuth,
	onAuthStateChanged,
	signInWithEmailAndPassword,
	signInAnonymously,
	onIdTokenChanged,
	createUserWithEmailAndPassword,
	signOut as fbSignOut,
	sendPasswordResetEmail,
	GoogleAuthProvider,
	signInWithPopup,
	signInWithRedirect,
	getRedirectResult,
	type Auth,
	type User,
} from "firebase/auth";

let app: FirebaseApp | null = null;
let auth: Auth | null = null;

export function initFirebase(): { app: FirebaseApp; auth: Auth } {
	if (app && auth) return { app, auth };
	app = initializeApp({
		apiKey:            import.meta.env.VITE_FIREBASE_API_KEY,
		authDomain:        import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
		projectId:         import.meta.env.VITE_FIREBASE_PROJECT_ID,
		appId:             import.meta.env.VITE_FIREBASE_APP_ID,
		storageBucket:     import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
		messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
	});
	auth = getAuth(app);
	return { app, auth };
}

// Auth state observer. Returns an unsubscribe function. The handler fires
// once on subscription with the cached state (User or null) and again on
// every transition (sign-in / sign-out / token refresh).
export function subscribeAuth(handler: (user: User | null) => void): () => void {
	const { auth } = initFirebase();
	return onAuthStateChanged(auth, handler);
}

/** ASK-172 Client 5e item 0b — the profile's ID-token stream (fires BEFORE onAuthStateChanged, and — registered at boot —
 *  before the Realtime Database's own token listener, which registers only when the room opens). The presence room uses it
 *  to drop its socket while the server still holds the OLD identity, so the room's onDisconnect removal runs as the uid that
 *  owns the node (measured: once the database had re-authed as another account, the guest's node outlived the tab). */
export function subscribeIdToken(handler: (user: User | null) => void): () => void {
	const { auth } = initFirebase();
	return onIdTokenChanged(auth, handler);
}

// Google sign-in — popup first (stays in the 3D context), automatic redirect
// fallback when the environment blocks popups (in-app webviews, strict
// browsers). The redirect leg completes via consumeGoogleRedirect() on the
// next page load. GCG's ASK-066 beforeCreate allowlist gates this provider
// exactly like email/password — no lockdown bypass.
export async function signInWithGoogle(): Promise<User> {
	const { auth } = initFirebase();
	const provider = new GoogleAuthProvider();
	provider.setCustomParameters({ prompt: "select_account" });
	try {
		const result = await signInWithPopup(auth, provider);
		return result.user;
	} catch (err: unknown) {
		const code = (err as { code?: string })?.code || "";
		if (code === "auth/popup-blocked" || code === "auth/popup-closed-by-user" || code === "auth/operation-not-supported-in-this-environment") {
			if (code === "auth/popup-blocked" || code === "auth/operation-not-supported-in-this-environment") {
				await signInWithRedirect(auth, provider);
				// Page navigates away; this promise never resolves.
				return new Promise<User>(() => {});
			}
		}
		throw err;
	}
}

// Completes a redirect-mode Google sign-in on page load (no-op otherwise).
export async function consumeGoogleRedirect(): Promise<User | null> {
	const { auth } = initFirebase();
	try {
		const result = await getRedirectResult(auth);
		return result?.user ?? null;
	} catch (err) {
		console.warn("[auth] google redirect completion failed:", (err as Error)?.message);
		return null;
	}
}

export async function signIn(email: string, password: string): Promise<User> {
	const { auth } = initFirebase();
	const result = await signInWithEmailAndPassword(auth, email, password);
	return result.user;
}

// ASK-172 Client 5b — a GUEST: Firebase anonymous auth, offered ONLY by the gate on a meet link where the plane's public
// door says `presence.anonymousMayFollow` (dev true · prod false — read, never assumed). A guest reads, never writes.
export async function signInAsGuest(): Promise<User> {
	const { auth } = initFirebase();
	const result = await signInAnonymously(auth);
	return result.user;
}

export async function signUp(email: string, password: string): Promise<User> {
	const { auth } = initFirebase();
	const result = await createUserWithEmailAndPassword(auth, email, password);
	return result.user;
}

export async function signOut(): Promise<void> {
	const { auth } = initFirebase();
	await fbSignOut(auth);
}

export async function resetPassword(email: string): Promise<void> {
	const { auth } = initFirebase();
	await sendPasswordResetEmail(auth, email);
}

// Returns the current user's Firebase ID token (a short-lived JWT, ~1h TTL,
// auto-refreshed by the SDK in the background). Passed in the
// `Authorization: Bearer ...` header on `POST /shops/{shopId}/sessions`
// so GCG (once iter-5a lands) can map the request to a customerId.
// Returns null when the customer isn't signed in.
//
// Pass forceRefresh=true to bypass the SDK's local cache and fetch a
// brand-new token — used by the pending-approval poller to detect custom
// claim changes (the `approved: true` flag set by the admin's email click).
export async function getIdToken(forceRefresh = false): Promise<string | null> {
	const { auth } = initFirebase();
	const user = auth.currentUser;
	if (!user) return null;
	return user.getIdToken(forceRefresh);
}

// Returns the parsed custom claims on the current user's ID token, or
// null when signed out. Force-refreshes the token first so newly-set
// claims (admin approval, role changes) reach the client without
// waiting on the SDK's ~1h cache TTL.
export async function getIdTokenClaims(forceRefresh = false): Promise<Record<string, unknown> | null> {
	const { auth } = initFirebase();
	const user = auth.currentUser;
	if (!user) return null;
	const result = await user.getIdTokenResult(forceRefresh);
	return result.claims;
}

// User-facing copy for the most common Firebase Auth error codes. Anything
// not listed gets the default fallback — keeps the surface stable while we
// learn which codes real users see.
import { allowlistRejectionCopy } from "./access-mode";

export function authErrorCopy(code: string): string {
	const allowlistCopy = allowlistRejectionCopy(code);
	if (allowlistCopy) return allowlistCopy;
	switch (code) {
		case "auth/invalid-email":         return "That doesn't look like a valid email.";
		case "auth/missing-password":      return "Enter a password.";
		case "auth/weak-password":         return "Password is too weak. Use at least 6 characters.";
		case "auth/email-already-in-use":  return "An account with this email already exists. Sign in instead?";
		case "auth/invalid-credential":
		case "auth/wrong-password":
		case "auth/user-not-found":        return "Email or password didn't match. Try again or reset your password.";
		case "auth/too-many-requests":     return "Too many attempts. Wait a minute or reset your password.";
		case "auth/network-request-failed":return "Cannot reach the sign-in service — check your connection, then Retry.";
		case "auth/account-exists-with-different-credential":
			return "This email already has an account with a password. Sign in with your email and password instead.";
		case "auth/popup-closed-by-user":  return "Google sign-in was cancelled. Try again when ready.";
		default:                           return "Something went wrong. Please try again.";
	}
}
